/**
 * Beautify JSON, XML, and HTML body text.
 */
import { reindentJson, hasUnsafeJsonNumber } from './jsonReindent';
import { formatXml } from './xmlFormat';

// Native parse → stringify is ~4x faster than the text re-indenter on a large
// body and prints the same thing for ordinary JSON. Only a body holding a
// number JS would round (> 15 digits, or an exponent) takes the re-indenter,
// so integers above 2^53 keep every digit (see utils/jsonReindent.js).
export function formatJson(value) {
  const text = String(value);
  if (hasUnsafeJsonNumber(text)) return reindentJson(text);
  return JSON.stringify(JSON.parse(text), null, 2);
}

/**
 * Indent HTML-like markup (XML has its own token-based formatter, xmlFormat.js).
 */
export function formatMarkup(value) {
  const compact = String(value)
    .replace(/>\s+</g, '><')
    .replace(/\r\n/g, '\n')
    .trim();
  if (!compact) return '';

  const lines = compact
    .replace(/></g, '>\n<')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  let depth = 0;
  return lines
    .map((line) => {
      if (/^<\//.test(line)) {
        depth = Math.max(0, depth - 1);
      }
      const indented = `${'  '.repeat(depth)}${line}`;
      if (/^<[^!?/][^>]*[^/]>$/.test(line) && !/^<(?:meta|link|img|br|hr|input|source)\b/i.test(line)) {
        depth += 1;
      }
      return indented;
    })
    .join('\n');
}

export function canFormatCode(language) {
  return language === 'json' || language === 'xml' || language === 'html';
}

/**
 * @returns {{ ok: true, value: string } | { ok: false, error: Error }}
 */
export function formatCode(language, value) {
  const text = String(value ?? '');
  if (!text.trim()) {
    return { ok: true, value: text };
  }
  try {
    if (language === 'json') {
      return { ok: true, value: formatJson(text) };
    }
    if (language === 'xml') {
      const formatted = formatXml(text);
      return formatted === null ? { ok: false, error: new Error('Could not format XML') } : { ok: true, value: formatted };
    }
    if (language === 'html') {
      return { ok: true, value: formatMarkup(text) };
    }
    return { ok: false, error: new Error('Unsupported language') };
  } catch (error) {
    return { ok: false, error };
  }
}
